import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTrialProspectMutations } from "@/hooks/useTrialProspects";
import { useStateDraft } from "@/hooks/useStateDraft";
import { TrialSlotCalendar, type TrialSlotSelection } from "@/components/trials/TrialSlotCalendar";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}

export function AddTrialProspectDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation("trials");
  const { create } = useTrialProspectMutations();

  // Champs consolidés en un objet unique : c'est ce qui permet au brouillon
  // persisté (useStateDraft) de couvrir tout le formulaire d'un bloc.
  const EMPTY_FORM = {
    firstName: "", lastName: "", email: "", phone: "",
    classId: "", notes: "", source: "", clothingSize: "",
  };
  const [formData, setFormData] = useState(EMPTY_FORM);
  const handleChange = (field: keyof typeof EMPTY_FORM, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }));
  // Données serveur — jamais dans le brouillon.
  const [selectedSlot, setSelectedSlot] = useState<TrialSlotSelection | null>(null);

  const { clearDraft, applyDraft } = useStateDraft("add-trial-prospect", formData, setFormData, {
    isEmpty: d => !d.firstName && !d.lastName && !d.email && !d.phone,
  });

  // La fermeture (Escape/overlay comprise) blanchit les champs mais CONSERVE
  // le brouillon : à la réouverture dans la même session, on le ré-applique.
  useEffect(() => {
    if (open) applyDraft();
  }, [open, applyDraft]);

  const reset = () => {
    setFormData(EMPTY_FORM);
    setSelectedSlot(null);
  };

  const submit = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) return toast.error(t("firstName") + " / " + t("lastName"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return toast.error(t("email"));
    if (!selectedSlot) return toast.error(t("selectClass"));
    try {
      await create.mutateAsync({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        class_id: selectedSlot.classId,
        class_date: selectedSlot.classDate,
        notes: formData.notes.trim() || undefined,
        source: formData.source.trim() || undefined,
        clothing_size: formData.clothingSize.trim() || undefined,
      });
      toast.success(t("toasts.created"));
      clearDraft();
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (e: any) {
      toast.error(e?.message ?? "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-3xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("newProspect")}</DialogTitle>
          <DialogDescription>{t("subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("firstName")}</Label>
              <Input value={formData.firstName} onChange={e => handleChange("firstName", e.target.value)} />
            </div>
            <div>
              <Label>{t("lastName")}</Label>
              <Input value={formData.lastName} onChange={e => handleChange("lastName", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("email")}</Label>
              <Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} />
            </div>
            <div>
              <Label>{t("phone")}</Label>
              <Input value={formData.phone} onChange={e => handleChange("phone", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>{t("class")}</Label>
            <TrialSlotCalendar
              value={selectedSlot}
              onChange={(slot) => {
                setSelectedSlot(slot);
                handleChange("classId", `${slot.classId}|${slot.classDate}`);
              }}
              futureOnly
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("source")}</Label>
              <Input value={formData.source} onChange={e => handleChange("source", e.target.value)} />
            </div>
            <div>
              <Label>{t("clothingSize")}</Label>
              <Input value={formData.clothingSize} onChange={e => handleChange("clothingSize", e.target.value)} placeholder="S / M / L / XL…" />
            </div>
          </div>
          <div>
            <Label>{t("notes")}</Label>
            <Textarea value={formData.notes} onChange={e => handleChange("notes", e.target.value)} rows={3} />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={() => { clearDraft(); onOpenChange(false); }} className="w-full sm:w-auto">{t("cancel")}</Button>
          <Button onClick={submit} disabled={create.isPending} className="w-full sm:w-auto">{t("create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
